import React, { FC, Fragment } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Button, Chip, Divider, IconButton, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Theme, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import MarkReadIcon from '@material-ui/icons/Drafts';
import ConfirmIcon from '@material-ui/icons/AssignmentTurnedIn';
import PendingConfirmIcon from '@material-ui/icons/AssignmentLate';
import RescheduleIcon from '@material-ui/icons/Restore';
import PhoneCallbackIcon from '@material-ui/icons/PhoneCallback';
import ActionTagIcon from '@material-ui/icons/Flag';
import { addDays, differenceInMinutes, format, startOfDay } from 'date-fns';

interface Props {
  notification: NotificationModel;
  showDivider: boolean;
  now: Date;
  onOpen: (notification: NotificationModel) => void;
  onResolve: (notifId: number) => void;
  onUndo: (notifId: number) => void;
  onMarkRead: (notifId: number) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  // Once a row has a secondary action, MUI wraps it in a container and the action becomes a SIBLING
  // of the row rather than a child, so the row's own hover rule would stop applying the moment the
  // pointer reached the buttons. Watching the container keeps the row lit the whole way across.
  itemContainer: {
    position: 'relative',
    '&:hover $item': { backgroundColor: '#F4F9FB' },
    '&:hover $unread': { backgroundColor: '#D2E7EF' },
    '&:hover $resolved': { backgroundColor: '#E7F6E7' }
  },
  item: {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25)
  },
  // Hovering deepens each of these in the container rule above rather than replacing them, so
  // pointing at a row never wipes the state it is showing.
  unread: { backgroundColor: '#E3F0F5' },
  resolved: { backgroundColor: '#F3FAF3' },
  iconSlot: {
    minWidth: 'auto',
    marginRight: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  // A non-colour cue for unread, so the state survives for colour-blind admins and does not rely on
  // the blue wash alone.
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    flex: 'none'
  },
  dotEmpty: { backgroundColor: 'transparent' },
  typeIcon: { color: theme.palette.primary.main, display: 'block' },
  typeIconAmber: { color: '#E09600' },
  typeIconAlert: { color: theme.palette.error.main },
  // Once handled, the icon stops asking for attention.
  typeIconMuted: { color: theme.palette.action.disabled },
  // The actions are absolutely positioned on the right, so the text has to stop short of them.
  text: {
    margin: 0,
    marginRight: theme.spacing(27),
    minWidth: 0
  },
  // A resolved row only carries Undo, so it gives most of that width back to the text.
  textResolved: { marginRight: theme.spacing(11) },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0
  },
  title: {
    flex: '0 1 auto',
    minWidth: 0,
    fontSize: 14,
    lineHeight: 1.45,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  titleUnread: { fontWeight: theme.typography.fontWeightMedium },
  muted: { color: theme.palette.text.secondary },
  time: {
    fontSize: 12,
    lineHeight: 1.45,
    color: theme.palette.text.secondary
  },
  sub: {
    fontSize: 12.5,
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  pill: {
    height: 20,
    flex: 'none',
    backgroundColor: theme.palette.completed.main,
    color: theme.palette.completed.contrastText,
    fontWeight: theme.typography.fontWeightMedium
  },
  pillIcon: { fontSize: 14, color: theme.palette.completed.contrastText, marginLeft: 5 },
  pillLabel: { paddingLeft: 4, paddingRight: 8, fontSize: 12 },
  // Always visible, so the row reserves width for them (see "text" above) rather than letting them
  // sit over the text.
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  actionBtn: {
    textTransform: 'none',
    padding: theme.spacing(0.25, 1),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  markReadBtn: { padding: theme.spacing(0.75) }
}));

export const formatNotificationDate = (notifDate: string): string => format(new Date(notifDate), 'EEE dd-MM-yyyy, hh:mm a');

/**
 * Roughly how long a resolved notification still has before it drops out of the feed: it stays
 * until midnight of the day it was resolved.
 *
 * Returns null when there is nothing meaningful to show - an unresolved notification, or one
 * resolved before "resolvedAt" was recorded, which the server keeps rather than expiring.
 */
const buildClearsInLabel = (resolvedAt: string | undefined, now: Date): string | null => {
  if (!resolvedAt) return null;

  const clearsAt = startOfDay(addDays(new Date(resolvedAt), 1));
  const minutesLeft = differenceInMinutes(clearsAt, now);

  if (minutesLeft <= 0) return 'clears shortly';
  if (minutesLeft < 60) return `clears in ${minutesLeft}m`;

  return `clears in ${Math.floor(minutesLeft / 60)}h`;
};

/**
 * Supporting line under the title. Once a notification is resolved, who handled it and how long it
 * has left is what an admin needs; the job itself is one click away.
 */
const buildSubLine = (notification: NotificationModel, now: Date): string => {
  if (notification.status !== 'resolved') return notification.description;

  // Rows resolved before "resolvedAt" existed fall back to "updatedAt", the closest approximation
  // available for them.
  const resolvedOn = format(new Date(notification.resolvedAt || notification.updatedAt), 'd MMM, h:mm a');
  const who = notification.resolvedBy ? `Resolved by ${notification.resolvedBy}` : 'Resolved';
  const clearsIn = buildClearsInLabel(notification.resolvedAt, now);

  return clearsIn ? `${who}, ${resolvedOn} · ${clearsIn}` : `${who}, ${resolvedOn}`;
};

const NotificationItem: FC<Props> = props => {
  const classes = useStyles(props);
  const { notification, showDivider, now, onOpen, onResolve, onUndo, onMarkRead } = props;

  const isResolved = notification.status === 'resolved';
  const isUnread = notification.status === 'unread';
  const canResolve = !isResolved && notification.type !== 'ACTIONTAG';

  // A resolved item keeps its type icon so the kind stays recognisable, only de-emphasised.
  const iconClass = clsx(classes.typeIcon, isResolved && classes.typeIconMuted);

  const renderIcon = () => {
    if (notification.type === 'CONFIRMED') return <ConfirmIcon fontSize='small' className={iconClass} />;
    if (notification.type === 'PENDING CONFIRMED') return <PendingConfirmIcon fontSize='small' className={iconClass} />;
    if (notification.type === 'RESCHEDULE')
      return <RescheduleIcon fontSize='small' className={clsx(iconClass, !isResolved && classes.typeIconAmber)} />;
    if (notification.type === 'CALLBACK') return <PhoneCallbackIcon fontSize='small' className={iconClass} />;
    if (notification.type === 'ACTIONTAG')
      return <ActionTagIcon fontSize='small' className={clsx(iconClass, !isResolved && classes.typeIconAlert)} />;
    return null;
  };

  return (
    <Fragment>
      <ListItem
        alignItems='center'
        ContainerProps={{ className: classes.itemContainer }}
        className={clsx(classes.item, isResolved ? classes.resolved : isUnread && classes.unread)}
        button
        onClick={() => onOpen(notification)}
      >
        <ListItemIcon className={classes.iconSlot}>
          <span className={clsx(classes.dot, !isUnread && classes.dotEmpty)} />
          {renderIcon()}
        </ListItemIcon>

        <ListItemText
          disableTypography
          className={clsx(classes.text, isResolved && classes.textResolved)}
          primary={
            <div className={classes.titleRow}>
              <span className={clsx(classes.title, isUnread && classes.titleUnread, isResolved && classes.muted)} title={notification.title}>
                {notification.title}
              </span>

              {isResolved && (
                <Chip
                  size='small'
                  icon={<CheckCircleIcon />}
                  label='Resolved'
                  className={classes.pill}
                  classes={{ icon: classes.pillIcon, label: classes.pillLabel }}
                />
              )}
            </div>
          }
          secondary={
            <Fragment>
              <div className={classes.sub}>{buildSubLine(notification, now)}</div>
              <div className={classes.time}>{formatNotificationDate(notification.createdAt)}</div>
            </Fragment>
          }
        />

        <ListItemSecondaryAction className={classes.actions}>
          {isUnread && (
            <Tooltip title='Mark as read'>
              <IconButton
                size='small'
                className={classes.markReadBtn}
                aria-label='Mark as read'
                onClick={e => {
                  e.stopPropagation(); // don't open job
                  onMarkRead(notification.id);
                }}
                color='primary'
              >
                <MarkReadIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          {isResolved && (
            <Button
              size='small'
              className={classes.actionBtn}
              onClick={e => {
                e.stopPropagation(); // don't open job
                onUndo(notification.id);
              }}
              color='primary'
            >
              Undo
            </Button>
          )}

          {canResolve && (
            <Button
              size='small'
              className={classes.actionBtn}
              onClick={e => {
                e.stopPropagation(); // don't open job
                onResolve(notification.id);
              }}
              color='primary'
            >
              Resolve
            </Button>
          )}

          {!isResolved && (
            <Button
              size='small'
              className={classes.actionBtn}
              onClick={e => {
                e.stopPropagation(); // the row handles this too; don't run it twice
                onOpen(notification);
              }}
              color='primary'
            >
              Open job
            </Button>
          )}
        </ListItemSecondaryAction>
      </ListItem>
      {showDivider && <Divider />}
    </Fragment>
  );
};

export default NotificationItem;
